import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
  IconButton,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { filesApi } from '@api/endpoints/files'
import type { UserFileDto } from '@api/types/files'
import { getImageUrl } from '@utils/imageUtils'

interface ServerImagePickerDialogProps {
  open: boolean
  onClose: () => void
  onSelect: (file: UserFileDto) => void
}

/**
 * Picks an image from the user's server media library to attach to a chat message. The chosen file
 * is copied server-side into a chat-owned image (chatApi.attachLibraryImage), so the original library
 * file is never affected by chat lifecycle cleanup.
 */
export function ServerImagePickerDialog({ open, onClose, onSelect }: ServerImagePickerDialogProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['chatLibraryImages'],
    queryFn: () =>
      filesApi.getFiles({ mimeTypePrefix: 'image/', pageSize: 60, sortBy: 'date', sortDesc: true }),
    enabled: open,
  })

  const files = data?.files ?? []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Choose from your library
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : files.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No images in your library yet.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
              gap: 1,
            }}
          >
            {files.map((file) => (
              <Box
                key={file.id}
                component="button"
                onClick={() => onSelect(file)}
                title={file.displayName || file.fileName}
                sx={{
                  p: 0,
                  border: 'none',
                  borderRadius: 1,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  aspectRatio: '1 / 1',
                  bgcolor: 'action.hover',
                  '&:hover': { outline: '2px solid', outlineColor: 'primary.main' },
                }}
              >
                <img
                  src={getImageUrl(file.thumbnailUrl || file.fileUrl) ?? undefined}
                  alt={file.displayName || file.fileName}
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  )
}
